// Zod schemas IS the contract.
import { z } from 'zod';

export const Vec3Schema = z.object({ x: z.number(), y: z.number(), z: z.number() });
export const TileTypeSchema = z.enum([
  'start',
  'gold',
  'heal',
  'hazard',
  'item',
  'soul_anchor',
  'key_shop',
  'weapons_cache',
  'mystery_box',
  'event',
  'graveyard',
  'pathing',
]);
export const ConnectionSchema = z.object({
  targetId: z.string().min(1),
  type: z.enum(['walk', 'jump', 'teleport']),
});
export const BoardNodeSpecSchema = z.object({
  id: z.string().min(1),
  type: TileTypeSchema,
  position: Vec3Schema,
  parent: z.string().min(1).nullable().optional(),
  hostBridgeId: z.string().min(1).optional(),
  connections: z.array(ConnectionSchema),
  supportsGoal: z.boolean(),
  isFork: z.boolean().optional(),
});

/** Multiple bridges per pair allowed (§8 "Natural forks"). */
export const BridgeSpecSchema = z
  .object({
    id: z.string().min(1),
    fromIslandId: z.string().min(1),
    toIslandId: z.string().min(1),
    type: z.enum(['plank', 'rope', 'stone']),
    bridgeMultiplier: z.number().int().positive().optional(),
    /** True (default) = fades during Sky Quake. False = permanent route. */
    supportsSkyQuake: z.boolean().default(true),
  })
  .strict()
  .refine((bridge) => bridge.fromIslandId !== bridge.toIslandId, {
    message: 'Bridge cannot connect an island to itself',
    path: ['toIslandId'],
  });

export const DecorationSpecSchema = z.object({
  /** Must match a key in DECORATION_MODEL_REGISTRY (Task 3.1). */
  modelKey: z.enum([
    'tree-birch',
    'tree-pine',
    'tree-dead',
    'rock-small',
    'rock-medium',
    'rock-large',
    'ruin-arch',
    'ruin-pillar',
    'lantern',
    'floating-chunk-small',
    'floating-chunk-large',
  ]),
  position: Vec3Schema,
  rotationY: z.number().default(0),
  scale: z.number().positive().default(1),
  lockedToIsland: z.boolean().default(false),
  islandId: z.string().min(1).nullable().optional(),
  surfaceOffsetY: z.number().optional(),
  scaleWithIsland: z.boolean().default(false),
});

export const ToneMappingNameSchema = z.enum([
  'NoTone',
  'Linear',
  'Reinhard',
  'Cineon',
  'ACESFilmic',
  'AgX',
  'Neutral',
]);

export const FXJSONSchema = z
  .object({
    toneMapping: ToneMappingNameSchema.default('NoTone'),
    toneMappingExposure: z.number().nonnegative().default(1),
    bloomStrength: z.number().nonnegative().default(0.6),
    bloomRadius: z.number().nonnegative().default(0.85),
    bloomThreshold: z.number().min(0).max(1).default(0.85),
  })
  .default({});

export const CameraStateJSONSchema = z.object({
  position: z.tuple([z.number(), z.number(), z.number()]),
  target: z.tuple([z.number(), z.number(), z.number()]),
  fov: z.number().positive().max(120),
});

export const CameraJSONSchema = z.object({
  lobby: CameraStateJSONSchema,
  editor: CameraStateJSONSchema.optional(),
  customizationFront: CameraStateJSONSchema.optional(),
  customizationBack: CameraStateJSONSchema.optional(),
});

export const DaylightSkyJSONSchema = z
  .object({
    sunAzimuthDeg: z.number().default(45),
    sunElevationDeg: z.number().min(-5).max(90).default(52),
    turbidity: z.number().nonnegative().default(3),
    rayleigh: z.number().nonnegative().default(1.6),
    mieCoefficient: z.number().nonnegative().default(0.004),
    mieDirectionalG: z.number().min(0).max(0.999).default(0.76),
  })
  .default({});

export const CloudLayerJSONSchema = z
  .object({
    shape: z.literal('snow_globe').default('snow_globe'),
    color: z.number().int().nonnegative().default(0xffffff),
    opacity: z.number().min(0).max(1).default(1),
    density: z.number().min(0).max(1).default(0.4),
    noiseScale: z.number().positive().default(3),
    drift: z.object({ x: z.number().default(0.05), y: z.number().default(0) }).default({}),
    height: z.number().default(-5),
    size: z.number().positive().default(120),
  })
  .default({});

export const LightingShadowJSONSchema = z
  .object({
    enabled: z.boolean().default(false),
    mapSize: z.number().int().positive().default(1024),
    cameraSize: z.number().positive().default(48),
    near: z.number().positive().default(0.5),
    far: z.number().positive().default(120),
    bias: z.number().default(-0.0005),
    normalBias: z.number().default(0.02),
  })
  .default({});

/** FogExp2 (color + density) plus daylight sky/cloud/lighting atmosphere tuning. */
export const ThemeJSONSchema = z.object({
  skyTopColor: z.number().int().nonnegative(),
  skyBottomColor: z.number().int().nonnegative(),
  sky: DaylightSkyJSONSchema,
  clouds: CloudLayerJSONSchema,
  fogColor: z.number().int().nonnegative(),
  fogDensity: z.number().nonnegative(),
  lightingRig: z.object({
    keyIntensity: z.number().nonnegative(),
    keyColor: z.number().int().nonnegative(),
    fillIntensity: z.number().nonnegative(),
    fillColor: z.number().int().nonnegative(),
    rimSkyColor: z.number().int().nonnegative(),
    rimGroundColor: z.number().int().nonnegative(),
    rimIntensity: z.number().nonnegative(),
    shadow: LightingShadowJSONSchema,
  }),
});

export const DecorationsJSONSchema = z.object({
  decorations: z.array(DecorationSpecSchema).max(120),
  crystals: z.array(Vec3Schema).max(20).default([]),
  fountainAnchors: z.array(Vec3Schema).max(10).default([]),
});

/** Optional per-island placement override. When present, overrides auto-computed values. */
export const IslandOverrideSchema = z.object({
  radius: z.number().positive().optional(),
  center: Vec3Schema.optional(),
});

/** Top-level. `nodes` is also validated by src/shared/board/map-loader.ts loadMap(). */
export const SkyforgeSchemaSchema = z.object({
  name: z.string(),
  version: z.number().int().min(1),
  bridges: z.array(BridgeSpecSchema).max(60),
  nodes: z.array(BoardNodeSpecSchema),
  startingTreasureChestNodeId: z.string().optional(),
  startingWeaponsCacheNodeId: z.string().nullable().optional(),
  spawnNodeIds: z.array(z.string()).min(1),
  /**
   * Optional manual radius overrides per island. Key is the island ID (e.g. "island-1").
   * When absent for an island, auto-sizing is used (count + spread based).
   */
  islandOverrides: z.record(z.string(), IslandOverrideSchema).optional(),
  /**
   * Spectator-arena designation. The island whose ID matches this field becomes the
   * warmup/quick-match staging arena: portal-arrived users (and native landing users)
   * spawn here, free-roam, fight with items, and watch the active main game in the
   * background. When 4 players are gathered, they're matched into a real game room.
   * If absent OR points to an unknown island, server falls back to the static-lobby flow.
   */
  spectatorArenaIslandId: z.string().optional(),
  /**
   * Spawn nodes within the spectator arena island (max 4 — arena cap is 4 players).
   * If absent, server uses the first 4 'start'-type nodes on the arena island.
   */
  spectatorArenaSpawnNodeIds: z.array(z.string()).max(4).optional(),
});

export type SkyforgeSchema = z.infer<typeof SkyforgeSchemaSchema>;
export type ThemeJSON = z.infer<typeof ThemeJSONSchema>;
export type DecorationsJSON = z.infer<typeof DecorationsJSONSchema>;
export type FXJSON = z.infer<typeof FXJSONSchema>;
export type CameraJSON = z.infer<typeof CameraJSONSchema>;
export type CameraStateJSON = z.infer<typeof CameraStateJSONSchema>;
export type BridgeSpec = z.infer<typeof BridgeSpecSchema>;
export type BoardNodeSpec = z.infer<typeof BoardNodeSpecSchema>;
export type DecorationSpec = z.infer<typeof DecorationSpecSchema>;
export type IslandOverride = z.infer<typeof IslandOverrideSchema>;
